import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import './QueueForm.css';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import VinInspectionGrid from './VinInspectionGrid';
import UploadReviewForm from './UploadReviewForm';
import { InputText } from 'primereact/inputtext';
import { Toolbar } from 'primereact/toolbar';
import { Dropdown } from 'primereact/dropdown';
import { SplitButton } from 'primereact/splitbutton';
import { Menu } from 'primereact/menu';
import { apiCallErrorHandler } from '../utils/common';
import { downloadXlsx } from '../utils/pdfUtils';

const reviewedFilterOptions = [
  { label: 'All', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const buildFiltersPayload = (filters) => {
  const payload = {};
  if (!filters) return payload;

  const toPascal = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  Object.keys(filters).forEach((key) => {
    const filter = filters[key];
    if (!filter) return;
    const value = Array.isArray(filter) ? filter[0]?.value : filter.value;
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      // don't send VIN unless it's exactly 17 characters
      if (key === 'vin') {
        const s = String(value).trim();
        if (s.length === 17) {
          payload.Vin = s;
        }
        return;
      }
      // convert other filter keys to PascalCase to match backend
      payload[toPascal(key)] = value;
    }
  });

  return payload;
};

export default function SessionsForm() {
  const toast = useRef(null);
  const toastShown = useRef(false);
  const downloadMenuRef = useRef(null);
  const { getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.toast && !toastShown.current) {
      toastShown.current = true;
      const { severity, summary, detail } = location.state.toast;
      toast.current?.show({ severity, summary, detail, life: 4000 });
      navigate(location.pathname, { replace: true, state: {} });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    sortField: "startedAt",
    sortOrder: -1, // default desc
    filters: {},
  });
  const [totalRecords, setTotalRecords] = useState(0);
  useEffect(() => { document.title = 'OptiConfig Upload Sessions'; }, []);

  // VIN quick filter (text input)
  const [vinFilter, setVinFilter] = useState('');
  const vinError = vinFilter.length > 0 && vinFilter.length !== 17;

  // Inspect dialog state
  const [inspectVisible, setInspectVisible] = useState(false);
  const [inspectSessionId, setInspectSessionId] = useState(null);

  // Cost estimation dialog state
  const [costDialogVisible, setCostDialogVisible] = useState(false);
  const [costDialogRow, setCostDialogRow] = useState(null);
  const [costData, setCostData] = useState([]);
  const [costFetching, setCostFetching] = useState(false);

  // Upload review dialog state
  const [reviewDialogVisible, setReviewDialogVisible] = useState(false);
  const [reviewTargetSessionId, setReviewTargetSessionId] = useState(null);
  const [reviewTargetBatchTitle, setReviewTargetBatchTitle] = useState(null);

  // Row selection state
  const [selectedRow, setSelectedRow] = useState(null);

  // Reviewed filter state
  const [reviewedFilter, setReviewedFilter] = useState(null);

  useEffect(() => {
    loadLazyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyParams]);

  const showToast = (severity, summary, detail) => {
    if (toast.current) {
      toast.current.show({ severity, summary, detail, life: 3000 });
    }
  };

  const loadLazyData = useCallback(async () => {
    setFetching(true);
    try {
      const token = await getAccessTokenSilently();

      // Use current lazyParams for API call
      const page = Math.floor(lazyParams.first / lazyParams.rows);
      const params = new URLSearchParams({
        page: String(page),
        size: String(lazyParams.rows),
      });

      // Handle sorting
      let effectiveSortOrder = null;
      if (lazyParams.sortOrder !== null && lazyParams.sortOrder !== undefined) {
        if (typeof lazyParams.sortOrder === 'number') {
          effectiveSortOrder = lazyParams.sortOrder === 1 ? 'asc' : 'desc';
        } else if (typeof lazyParams.sortOrder === 'string') {
          const s = lazyParams.sortOrder.toLowerCase().trim();
          effectiveSortOrder = s === 'asc' ? 'asc' : 'desc';
        }
      }

      if (lazyParams.sortField) params.append('sortField', lazyParams.sortField);
      if (effectiveSortOrder) params.append('sortOrder', effectiveSortOrder);

      // Handle filters
      const filtersObj = buildFiltersPayload(lazyParams.filters);
      if (Object.keys(filtersObj).length > 0) {
        params.append('filters', JSON.stringify(filtersObj));
      }

      // Call sessions endpoint
      const url = `${API_BASE_URL}/session/list?${params.toString()}`;

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) await apiCallErrorHandler(res, 'Failed to load sessions');

      const payload = await res.json();

      setRows(payload.data);
      setTotalRecords(payload.total);
      setSelectedRow(null);
    } catch (err) {
      showToast('error', 'Error', err.message || 'Failed to load sessions');
      setRows([]);
      setTotalRecords(0);
    } finally {
      setFetching(false);
    }
  }, [getAccessTokenSilently, lazyParams]);

  // Separate handlers for different events
  const onPage = (event) => {
    setLazyParams(prev => ({
      ...prev,
      first: event.first,
      rows: event.rows,
    }));
  };

  const onSort = (event) => {
    const currentSortOrder = lazyParams.sortOrder;
    const currentSortField = lazyParams.sortField;
    
    let newSortOrder = event.sortOrder;
    
    // Handle sort toggle if clicking the same field
    if (event.sortField === currentSortField) {
      newSortOrder = currentSortOrder === 1 ? -1 : 1;
    }

    setLazyParams(prev => ({
      ...prev,
      first: 0, // Reset to first page when sorting
      sortField: event.sortField,
      sortOrder: newSortOrder,
    }));
  };

  const onFilter = (event) => {
    setLazyParams(prev => {
      const merged = { ...event.filters };
      // Preserve external filters (managed outside the DataTable column filter system)
      // only when they carry a real value; otherwise let them be absent so they are not sent
      if (prev.filters?.isReviewed?.value != null) {
        merged.isReviewed = prev.filters.isReviewed;
      } else {
        delete merged.isReviewed;
      }
      if (prev.filters?.vin?.value) {
        merged.vin = prev.filters.vin;
      } else {
        delete merged.vin;
      }
      return { ...prev, first: 0, filters: merged };
    });
  };

  const formatDate = (dateString) => {
    return dateString ? moment.utc(dateString).local().format('DD.MM.YY HH:mm') : '';
  };

  const renderHeader = (label, field) => {
    const isSorted = lazyParams.sortField === field;
    const order = lazyParams.sortOrder;
    const arrow = isSorted
      ? (order === 1 || String(order).toLowerCase() === 'asc' ? '▲' : '▼')
      : '';
    return (
      <div className="col-header">
        <span className="col-label">{label}</span>
        <span className="col-sort">{arrow}</span>
      </div>
    );
  };

  // open session config in new tab
  const handleOpen = (sessionId) => {
    if (!sessionId) return;
    window.open(`/config/${sessionId}`, '_blank', 'noopener,noreferrer');
  };

  // discard session via API
  const handleDelete = async (sessionId, isReview = false) => {
    if (!sessionId) return;
    const confirmMsg = isReview
      ? 'Delete the review for this session? This action cannot be undone.'
      : 'Delete this session? This action cannot be undone.';
    if (!window.confirm(confirmMsg)) return;
    setFetching(true);
    try {
      const token = await getAccessTokenSilently();
      const url = `${API_BASE_URL}/file/discard/${sessionId}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) await apiCallErrorHandler(res, isReview ? 'Failed to delete the review' : 'Failed to delete the session');
      showToast('success', 'Success', isReview ? 'Review removed' : 'Session deleted');
      await loadLazyData();
    } catch (err) {
      showToast('error', 'Error', err.message || (isReview ? 'Failed to remove the review' : 'Failed to delete the session'));
    } finally {
      setFetching(false);
    }
  };

  const onVinFilterChange = (e) => {
    const v = (e.target?.value ?? '').trim();

    setVinFilter(v);

    if (!(v.length === 17 || v.length === 0))
      return;

    setLazyParams(prev => {
      const { vin: _removed, ...rest } = prev.filters ?? {};
      return {
        ...prev,
        first: 0,
        filters: v.length === 17 ? { ...rest, vin: { value: v } } : rest,
      };
    });
  };

  const onReviewedFilterChange = (e) => {
    const v = e.value;
    setReviewedFilter(v);
    setLazyParams(prev => {
      const { isReviewed: _removed, ...rest } = prev.filters ?? {};
      return {
        ...prev,
        first: 0,
        // Only keep isReviewed in filters when a real value (true/false) is selected;
        // null means "All" which removes the filter entirely
        filters: v != null ? { ...rest, isReviewed: { value: v } } : rest,
      };
    });
  };

  const handleUploadReview = (sessionId, batchTitle) => {
    if (!sessionId) return;
    setReviewTargetSessionId(sessionId);
    setReviewTargetBatchTitle(batchTitle ?? null);
    setReviewDialogVisible(true);
  };

  const closeReviewDialog = () => {
    setReviewDialogVisible(false);
    setReviewTargetSessionId(null);
    setReviewTargetBatchTitle(null);
  };

  const handleReviewSuccess = () => {
    closeReviewDialog();
    showToast('success', 'Success', 'Review uploaded successfully.');
    loadLazyData();
  };

  const openInspect = (sessionId) => {
    if (!sessionId) return;
    setInspectSessionId(sessionId);
    setInspectVisible(true);
  };

  const closeInspect = () => {
    setInspectVisible(false);
    setInspectSessionId(null);
  };

  const openCostDialog = async (row) => {
    setCostDialogRow(row);
    setCostData([]);
    setCostDialogVisible(true);
    setCostFetching(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE_URL}/session/cost-estimation/${row.sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) await apiCallErrorHandler(res, 'Failed to load cost estimation');
      setCostData(await res.json());
    } catch (err) {
      showToast('error', 'Error', err.message || 'Failed to load cost estimation');
    } finally {
      setCostFetching(false);
    }
  };

  const closeCostDialog = () => {
    setCostDialogVisible(false);
    setCostDialogRow(null);
    setCostData([]);
  };

  const handleDownloadOptiConfig = async (sessionId) => {
    setFetching(true);
    try {
      const token = await getAccessTokenSilently();
      await downloadXlsx(sessionId, token);
    } catch (err) {
      showToast('error', 'Error', err.message || 'Download failed');
    } finally {
      setFetching(false);
    }
  };

  const handleDownloadSessionFile = async (sessionId, type, batchTitle) => {
    setFetching(true);
    try {
      const token = await getAccessTokenSilently();

      if (type === 'original') {
        const res = await fetch(`${API_BASE_URL}/file/download-xlsx/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) await apiCallErrorHandler(res, 'Failed to download file');
        const downloadName = selectedRow?.fileName || `batch_${batchTitle || sessionId}_original.xlsx`;
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      if (type === 'review') {
        const res = await fetch(`${API_BASE_URL}/file/download-xlsx-review/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) await apiCallErrorHandler(res, 'Failed to download review file');
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `batch_${batchTitle || sessionId}_review.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      if (type === 'review-opticonfig') {
        const res = await fetch(`${API_BASE_URL}/file/download-xlsx-done-reviewed/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) await apiCallErrorHandler(res, 'Failed to download OptiConfig with review');
        const blob = await res.blob();
        const disposition = res.headers.get('Content-Disposition');
        const match = disposition && disposition.match(/filename[^;=\n]*=['"']?([^'"\n;]+)['"']?/i);
        const filename = match ? match[1] : `batch_${batchTitle || sessionId}_full_reviewed.xlsx`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      // For mapped/abu we need session data
      const sessionRes = await fetch(`${API_BASE_URL}/file/load-session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!sessionRes.ok) await apiCallErrorHandler(sessionRes, 'Failed to load session data');
      const { sessionData, processFileData } = await sessionRes.json();

      const body = {
        fileName: sessionData?.request?.fileName,
        worksheet: sessionData?.request?.worksheet,
        mapping: sessionData?.mapping ?? processFileData?.mapping ?? {},
        grouping: sessionData?.grouping ?? [],
        source: sessionData?.enrichmentSource ?? 'SUPPLIER',
      };

      const endpoint = `${API_BASE_URL}/file/download-abu/${sessionId}`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) await apiCallErrorHandler(res, 'Failed to download file');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch_${batchTitle || sessionId}_abu.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast('error', 'Error', err.message || 'Download failed');
    } finally {
      setFetching(false);
    }
  };

  const canInspect = vinFilter.length === 17 && rows && rows.length > 0;

  const thStyle = { padding: '6px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #ccc' };
  const tdStyle = { padding: '5px 10px' };

  return (
    <div className="queue-form card">
      <Toast ref={toast} />
      <h3 style={{ textAlign: 'center', marginBottom: '1rem' }}>Session list</h3>

      <div className="p-mt-4">
        <div style={{ marginBottom: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button
            type="button"
            icon="pi pi-plus"
            label="Upload a new batch"
            className="p-button-sm p-button-text"
            onClick={() => navigate('/config')}
          />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label htmlFor="vinFilter" style={{ marginRight: '0.25rem', fontSize: '0.85rem' }}>VIN:</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <InputText id="vinFilter" value={vinFilter} onChange={onVinFilterChange} placeholder="Filter by VIN" />
              {vinError && (
                <span style={{ color: 'red', fontSize: '0.85rem' }}>
                  VIN number should contain 17 symbols
                </span>
              )}
            </div>
          </div>
        </div>

        {selectedRow && (
          <Toolbar
            style={{ marginBottom: '0.5rem', padding: '0.4rem 0.75rem', minHeight: 'auto', fontSize: '0.8rem' }}
            start={
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <Button
                  type="button"
                  icon="pi pi-external-link"
                  label="Open"
                  className="p-button-text p-button-sm"
                  onClick={() => handleOpen(selectedRow.sessionId)}
                />
                <Menu
                  ref={downloadMenuRef}
                  popup
                  style={{ minWidth: '260px' }}
                  model={[
                    {
                      label: 'Original',
                      icon: 'pi pi-download',
                      command: () => handleDownloadSessionFile(selectedRow.sessionId, 'original', selectedRow.batchTitle),
                    },
                    {
                      label: 'ABU',
                      icon: 'pi pi-download',
                      command: () => handleDownloadSessionFile(selectedRow.sessionId, 'abu', selectedRow.batchTitle),
                    },
                    {
                      label: 'OptiConfig',
                      icon: 'pi pi-download',
                      command: () => handleDownloadOptiConfig(selectedRow.sessionId),
                    },
                    ...(selectedRow.isReviewed ? [
                      {
                        label: 'Reviewed file',
                        icon: 'pi pi-file-check',
                        command: () => handleDownloadSessionFile(selectedRow.sessionId, 'review', selectedRow.batchTitle),
                      },
                      {
                        label: 'OptiConfig with review',
                        icon: 'pi pi-file-check',
                        style: { whiteSpace: 'nowrap' },
                        command: () => handleDownloadSessionFile(selectedRow.sessionId, 'review-opticonfig', selectedRow.batchTitle),
                      },
                    ] : []),
                  ]}
                />
                <Button
                  type="button"
                  icon="pi pi-download"
                  label="Download"
                  className="p-button-text p-button-sm"
                  onClick={(e) => downloadMenuRef.current?.toggle(e)}
                />
                <Button
                  type="button"
                  icon="pi pi-copy"
                  label="Copy Link"
                  className="p-button-text p-button-sm"
                  onClick={() => {
                    const url = `${window.location.origin}/config/${selectedRow.sessionId}`;
                    navigator.clipboard.writeText(url);
                    showToast('success', 'Copied', 'Session link copied to clipboard');
                  }}
                />
                {canInspect && (
                  <Button
                    type="button"
                    icon="pi pi-search"
                    label="Inspect VIN"
                    className="p-button-text p-button-sm"
                    onClick={() => openInspect(selectedRow.sessionId)}
                  />
                )}
                {selectedRow.isReviewed && (
                  <Button
                    type="button"
                    icon="pi pi-file-check"
                    label="Open Review"
                    className="p-button-text p-button-sm p-button-success"
                    onClick={() => window.open(`/review-compare/${selectedRow.sessionId}?batchTitle=${encodeURIComponent(selectedRow.batchTitle ?? '')}`, '_blank', 'noopener,noreferrer')}
                  />
                )}
                {selectedRow.isReviewed && (
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    label="Delete Review"
                    className="p-button-text p-button-sm"
                    style={{ color: 'red' }}
                    onClick={() => handleDelete(selectedRow.reviewSessionId, true)}
                  />
                )}
                <Button
                  type="button"
                  icon="pi pi-upload"
                  label="Upload Review"
                  className="p-button-text p-button-sm"
                  onClick={() => handleUploadReview(selectedRow.sessionId, selectedRow.batchTitle)}
                />
                <Button
                  type="button"
                  icon="pi pi-euro"
                  label="Show Cost"
                  className="p-button-text p-button-sm"
                  onClick={() => openCostDialog(selectedRow)}
                />
                {!selectedRow.isReviewed && (
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    label="Delete"
                    className="p-button-danger p-button-sm"
                    onClick={() => handleDelete(selectedRow.sessionId)}
                  />
                )}
              </div>
            }
            end={
              selectedRow.batchTitle && (
                <span style={{ fontWeight: 'normal', fontSize: '0.8rem', opacity: 0.8, marginRight: '0.5rem' }}>
                  {selectedRow.batchTitle}
                </span>
              )
            }
          />
        )}

        <DataTable
          value={rows}
          className="queue-table"
          loading={fetching}
          first={lazyParams.first}
          rows={lazyParams.rows}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          onFilter={onFilter}
          paginator
          rowsPerPageOptions={[10, 25, 50]}
          sortMode="single"
          filterDisplay="row"
          lazy
          style={{ fontSize: '0.65rem' }}
          rowClassName={(row) => row.isReviewed ? 'row-reviewed' : ''}
          selectionMode="single"
          selection={selectedRow}
          onSelectionChange={(e) => setSelectedRow(e.value)}
          dataKey="sessionId"
        >
          <Column selectionMode="single" headerStyle={{ width: '3rem' }} />
          <Column field="sessionId" header="SessionId" hidden />
          <Column field="userLogin" header={renderHeader('User', 'userLogin')} sortable filter filterPlaceholder="Filter by userLogin" />
          <Column style={{maxWidth: 350}} field="fileName" header={renderHeader('File', 'fileName')} sortable filter filterPlaceholder="Filter by fileName" />
          <Column field="worksheet" header={renderHeader('Worksheet', 'worksheet')} sortable filter filterPlaceholder="Filter by worksheet" />
          <Column style={{maxWidth: 350}} field="batchTitle" header={renderHeader('Batch Title', 'batchTitle')} sortable filter filterPlaceholder="Filter by batchTitle" />
          <Column field="batchSupplierInfo" header={renderHeader('Batch Info', 'batchSupplierInfo')} sortable filter filterPlaceholder="Filter by batchSupplierInfo" hidden />
          <Column field="startedAt" header={renderHeader('Started At', 'startedAt')} body={(row) => formatDate(row.startedAt)} sortable />
          <Column field="mappedCount" header={renderHeader('Mapped', 'mappedCount')} />
          <Column field="groupCount" header={renderHeader('Samples', 'groupCount')} />
          <Column field="sourceCount" header={renderHeader('Sources', 'sourceCount')} />
          <Column field="enrichedVinCount" header={renderHeader('Enriched VINs', 'enrichedVinCount')} />
          <Column
            field="isReviewed"
            header="Reviewed"
            body={(row) => row.isReviewed ? <i className="pi pi-check" style={{ color: 'green' }} /> : null}
            style={{ textAlign: 'center', width: '7rem' }}
            filter
            showFilterMenu={false}
            filterElement={() => (
              <Dropdown
                value={reviewedFilter}
                options={reviewedFilterOptions}
                optionLabel="label"
                optionValue="value"
                onChange={onReviewedFilterChange}
                placeholder="All"
                style={{ minWidth: '5rem', fontSize: '0.75rem' }}
              />
            )}
          />
        </DataTable>
        <Dialog
          header={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="pi pi-euro" />
                <span>Cost estimation</span>
              </div>
              {costDialogRow?.batchTitle && (
                <span style={{ fontWeight: 400, fontSize: '0.8rem', opacity: 0.7 }}>{costDialogRow.batchTitle}</span>
              )}
            </div>
          }
          visible={costDialogVisible}
          onHide={closeCostDialog}
          style={{ width: 600 }}
          modal
          dismissableMask
        >
          {costFetching ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}><i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }} /></div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f4f4f4' }}>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Cost/Request (€)</th>
                  <th style={thStyle}>Requests</th>
                  <th style={thStyle}>Total Cost (€)</th>
                </tr>
              </thead>
              <tbody>
                {costData.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={tdStyle}>{r.Source}</td>
                    <td style={tdStyle}>{Number(r.CostPerRequest).toFixed(2)}</td>
                    <td style={tdStyle}>{r.RequestCount}</td>
                    <td style={tdStyle}>{Number(r.TotalCostBySource).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {(() => {
                  const grandTotal = costData.reduce((sum, r) => sum + (Number(r.TotalCostBySource) || 0), 0);
                  const vinCount = costDialogRow?.enrichedVinCount || 0;
                  const costPerVin = vinCount > 0 ? (grandTotal / vinCount).toFixed(2) : '—';
                  return (
                    <>
                      <tr style={{ borderTop: '2px solid #aaa', fontWeight: 600, background: '#f9f9f9' }}>
                        <td style={tdStyle}>Total</td>
                        <td style={tdStyle}></td>
                        <td style={tdStyle}>{costData.reduce((sum, r) => sum + (Number(r.RequestCount) || 0), 0)}</td>
                        <td style={tdStyle}>{grandTotal.toFixed(2)}</td>
                      </tr>
                      <tr style={{ background: '#f9f9f9', fontStyle: 'italic' }}>
                        <td style={tdStyle} colSpan={2}>Cost per VIN ({vinCount} VINs)</td>
                        <td style={tdStyle}>{costPerVin}</td>
                      </tr>
                    </>
                  );
                })()}
              </tfoot>
            </table>
          )}
        </Dialog>

        <Dialog
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="pi pi-search" />
              <span>Standardized Equipment</span>
              {vinFilter && (
                <span style={{ fontWeight: 400, opacity: 0.8 }}>
                  — VIN {vinFilter}
                </span>
              )}
            </div>
          }
          visible={inspectVisible}
          onHide={closeInspect}
          style={{ width: '80vw', maxWidth: 1600, minWidth: 800, minHeight: 600 }}
          modal
          dismissableMask
        >
          {inspectVisible && inspectSessionId && (
            <VinInspectionGrid vin={vinFilter} sessionId={inspectSessionId} />
          )}
        </Dialog>

        <Dialog
          header={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="pi pi-upload" />
                <span>Upload Review</span>
              </div>
              {reviewTargetBatchTitle && (
                <span style={{ fontWeight: 400, fontSize: '0.8rem', opacity: 0.7 }}>{reviewTargetBatchTitle}</span>
              )}
            </div>
          }
          visible={reviewDialogVisible}
          onHide={closeReviewDialog}
          style={{ width: 560 }}
          modal
          dismissableMask
        >
          {reviewDialogVisible && reviewTargetSessionId && (
            <UploadReviewForm
              reviewedSessionId={reviewTargetSessionId}
              batchTitle={reviewTargetBatchTitle}
              onSuccess={handleReviewSuccess}
            />
          )}
        </Dialog>
      </div>
    </div>
  );
}